import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  Notification,
  NotificationVariants,
  Tooltip,
} from "@hid-galaxy-ui/galaxy-react";
import {
  botIcon,
  userIcon,
  documentIcon,
  chevronDownIcon,
  chevronUpIconLarge,
} from "./Icons";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getAuthToken } from "../utils";
import { useNavigate } from "react-router-dom";
import { LogoutPath } from "../routes";
import { API_BASE_URL, TENANT_ID } from "../utils/common";
import getFileNameFromUrl from "../__utils__/fileUtils";
import FeedbackModal from "./Feedback";
export enum Sender {
  USER = "user",
  BOT = "bot",
}

const ChatMessage = ({
  text,
  sender,
  isRequestId,
  userData,
  feedback: initialFeedback,
  isLastUserMsg = false,
  retryError = false,
  onRetry,
  sourceUrl = [],
}: {
  text: string;
  sender: Sender;
  isRequestId: any;
  userData?: any;
  feedback: any;
  isLastUserMsg?: boolean;
  retryError?: boolean;
  onRetry?: () => void;
  sourceUrl?: string[];
}) => {
  const [feedback, setFeedback] = useState<boolean | null>(initialFeedback);
  const [showMoreInsight, setShowMoreInsight] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreInsightValue, setMoreInsightValue] = useState("");
  const [notifications, setNotifications] = useState<any>([]);
  const [showAllReferences, setShowAllReferences] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (showAllReferences && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showAllReferences]);
  const submitLikeDislike = async (is_like: boolean) => {
    const payload = {
      user_id: userData?.user_id,
      is_like,
      feedback_comment: "",
      conversation_session_id: userData?.conversation_session_id,
      request_id: isRequestId,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/submitFeedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
          "x-tenant-id": TENANT_ID || "",
        },
        body: JSON.stringify(payload),
      });
      if (response.status === 401) {
        navigate(LogoutPath);
        return;
      }
    } catch (err) {
      console.error(err);
    }
  };
  const showSuccessNotification = () => {
    setNotifications([
      {
        icon: <Iconography icon="circleCheck" />,
        variant: NotificationVariants.Success,
        title: "Thank you! Your feedback has been submitted successfully.",
      },
    ]);
  };
  const isCustomMessage =
    sender === Sender.BOT &&
    typeof text === "string" &&
    text.includes("We’ve retrieved your chat for");
  const handleFeedback = (isLike: boolean) => {
    setFeedback(isLike);
    submitLikeDislike(isLike);
    setOpen(true);
  };
  const validSourceUrls = sourceUrl.filter(url =>  url.startsWith('https://')
  );
  const references =
    sender === Sender.BOT && validSourceUrls.length > 0 ? (
      <div
        className="references-container"
        ref={showAllReferences ? scrollRef : null}
      >
        <div className="references-main-row">
          {!showAllReferences && (
            <span className="references-title">AI-Curated :</span>
          )}

          {/* Show first 1-2 reference buttons only when NOT expanded */}
          {!showAllReferences && (
            <div className="references-buttons">
              {validSourceUrls.slice(0, 2).map((url, index) => {
                const fileName = getFileNameFromUrl(url) || "Unknown File";
                return (
                  <button
                    key={index}
                    className="reference-button"
                    onClick={() => window.open(url, "_blank")}
                    title={url}
                  >
                    {documentIcon()}
                    <div className="reference-separator"></div>
                    <span className="reference-filename">{fileName}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Show dropdown button only when collapsed and more than 1 reference */}
          {!showAllReferences && validSourceUrls.length > 1 && (
            <button
              className="reference-dropdown-toggle"
              onClick={() => setShowAllReferences(!showAllReferences)}
            >
              {chevronDownIcon()}
            </button>
          )}
        </div>

        {/* Dropdown menu */}
        {showAllReferences && validSourceUrls.length > 0 && (
          <div className="references-dropdown-content">
            {/* AI-Curated header with close button */}
            <div className="references-dropdown-header">
              <span className="references-dropdown-header-title">
                AI-Curated :
              </span>
              <button
                className="references-dropdown-close-btn"
                onClick={() => setShowAllReferences(false)}
              >
                {chevronUpIconLarge()}
              </button>
            </div>

            {/* Reference list */}
              {validSourceUrls.map((url, index) => {
              const fileName = getFileNameFromUrl(url) || "Unknown File";
              return (
                <div
                  key={index}
                  className="references-dropdown-item"
                  onClick={() => window.open(url, "_blank")}
                >
                  {documentIcon()}
                  <div className="references-dropdown-separator"></div>
                  <span className="references-dropdown-filename">
                    {fileName}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    ) : null;
  // ...existing code...
  const messageContent = () => (
    <div className={`message chat-message-${sender}`}>
      {sender === Sender.BOT ? botIcon() : null}
      <div className="message-content-wrapper">
        <div
          className={`chat-message-row ${
            sender === Sender.USER ? "user" : "bot"
          }`}
        >
          <div
            className={
              "hid-chat-card " +
              (isCustomMessage ? "hid-custom-message " : "") +
              (sender === Sender.USER
                ? "chat-message-user "
                : "chat-message-bot ")
            }
          >
            <div className="markdown-content">
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer">
                      {props.children}
                    </a>
                  ),
                  table: ({ node, ...props }) => (
                    <div className="markdown-table-wrapper">
                      <table {...props} />
                    </div>
                  ),
                }}
              >
                {text}
              </Markdown>
              {references}
            </div>
          </div>
          {sender === Sender.USER && userIcon()}
        </div>
        {sender === Sender.USER && isLastUserMsg && retryError && onRetry && (
          <div className="hid-retry-button">
            <span className="hid-retry-button-text">
              Connection issue occurred!
            </span>
            <Button
              variant="secondary"
              icon={<Iconography icon="arrowRotateLeft" />}
              label={"RETRY"}
              onClick={onRetry}
            />
          </div>
        )}
      </div>
    </div>
  );
  // ...existing code...

  const feedbackButtons = () => (
    <div className="chat-feedback-buttons">
      {feedback !== false && (
        <button
          data-testid="like-button"
          className={`hid-button-transparent hid-like-dislike ${
            feedback === true ? "hid-like-dislike--feedback" : ""
          }`}
          disabled={feedback === true}
          onClick={() => handleFeedback(true)}
        >
          <Iconography
            icon="thumbsUp"
            size={IconographySizes.Medium}
            className="feedback-Icon"
          />
        </button>
      )}
      {feedback !== true && (
        <button
          data-testid="dislike-button"
          className={`hid-button-transparent hid-like-dislike ${
            feedback === false ? "hid-like-dislike--feedback" : ""
          }`}
          disabled={feedback === false}
          onClick={() => handleFeedback(false)}
        >
          <Iconography icon="thumbsDown" size={IconographySizes.Medium} />
        </button>
      )}
    </div>
  );
  const submitFeedbackComment = async (comment: string) => {
    const payload = {
      user_id: userData?.user_id,
      is_like: feedback,
      feedback_comment: comment,
      conversation_session_id: userData?.conversation_session_id,
      request_id: isRequestId,
    };
    try {
      const response = await fetch(`${API_BASE_URL}/submitFeedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
          "x-tenant-id": TENANT_ID || "",
        },
        body: JSON.stringify(payload),
      });
      if (response.status === 401) {
        navigate(LogoutPath);
        return;
      }
      showSuccessNotification();
    } catch (err) {
      console.error("Error submitting feedback comment:", err);
    }
  };
  // Optimized feedback handler
  const handleFeedbackTagClick = React.useCallback(
    async (comment: string) => {
      await submitFeedbackComment(comment);
      setOpen(false);
    },
    [submitFeedbackComment]
  );

  return (
    <>
      {typeof text !== "string" ? (
        <div className={`message chat-message-${sender}`}>
          {sender === Sender.BOT ? botIcon() : userIcon()}
          <div className="hid-chat-card">{text}</div>{" "}
          {/* Render JSX directly */}
        </div>
      ) : (
        <>
          {messageContent()}
          {sender === Sender.BOT && feedbackButtons()}
          {open && (
            <FeedbackModal
              feedback={feedback}
              open={open}
              setOpen={setOpen}
              showMoreInsight={showMoreInsight}
              setShowMoreInsight={setShowMoreInsight}
              moreInsightValue={moreInsightValue}
              setMoreInsightValue={setMoreInsightValue}
              handleFeedbackTagClick={handleFeedbackTagClick}
            />
          )}
        </>
      )}
      {notifications.length > 0 && (
        <Notification toasts={notifications} setToasts={setNotifications} />
      )}
    </>
  );
};

export default ChatMessage;
